<template>
  <div class="v-atlas-raumplanung-feature-info">

    <TabsGroup v-if="entityData">
      <Tab label="Übersicht" tab-key="overview">
        <RaumplanungFeatureInfoOverview :featureInfo="entityData" :map-module="props.mapModule" />
      </Tab>
      <Tab label="KI-Bericht" tab-key="ai-report">
        <RaumplanungFeatureInfoAiReport :featureInfo="entityData" />
      </Tab>
    </TabsGroup>

    <LoadingIndicatorOverlay v-if="isLoadingDelayed" />
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, watch } from "vue";
import { RaumplanungMapModule } from "@/mapModules/raumplanung/RaumplanungMapModule";
import { useDelayedBoolean, useSmartFetch, LoadingIndicatorOverlay, ViewStackController, Tab, TabsGroup } from "gertrud";
import { useMessages } from "@/composables/useMessages";
import RaumplanungFeatureInfoAiReport from "./RaumplanungFeatureInfoAiReport.vue";
import RaumplanungFeatureInfoOverview from "./RaumplanungFeatureInfoOverview.vue";
import { RaumplanungFeatureInfoResponse } from "atlas-shared";


const props = defineProps<{
  featureId: string
  mapModule: RaumplanungMapModule
  viewStackController: ViewStackController
}>()



const entityInfoUrl = computed(() => {

  const entityId = props.featureId;
  if (!entityId) return "";

  const sstring = encodeURIComponent(JSON.stringify(props.mapModule.requestSettings))

  return props.mapModule?.moduleConfig?.serviceBaseUrl + "/entities/" + entityId + "?settings=" + sstring;
})

const { data: entityData, error, isLoading } = useSmartFetch<RaumplanungFeatureInfoResponse>(entityInfoUrl);

const isLoadingDelayed = useDelayedBoolean(isLoading, 350, 0);

const messages = useMessages();

watch(() => entityData.value, (newEntityData) => {
  if (newEntityData) {
    const top = props.viewStackController.topItem

    if (top) {
      top.title = newEntityData.entity?.["name"][0].value
    }

    // Forward messages included in the entityInfo response to the global notification system
    if (newEntityData.messages) {
      for (const m of newEntityData.messages) {
        messages.addMessage(m);
      }
    }
  }
})

/**
 * When the selected feature is deselected (set to null), remove this panel
 * from the view stack automatically. When another feature is selected
 * instead, the ViewStackController's addItem replacement logic handles it.
 *
 * Uses a module-scoped unique id so that each panel only ever finds itself,
 * even when other modules have feature-info panels on the stack.
 */
watch(() => props.mapModule.selectedFeatureId, (newSelectedId) => {
  if (newSelectedId === null) {
    const panelId = props.mapModule.uuid + "_feature-info"
    const idx = props.viewStackController.items.findIndex(i => i.id === panelId)
    if (idx !== -1) {
      props.viewStackController.popToIndex(idx)
      props.viewStackController.removeItemAtIndex(idx)
    }
  }
})

/**
 * When the user manually closes/navigates away from this panel (pop/back
 * in the ViewStack), unselect the feature in the map module so that the
 * highlight is cleared and the module state stays consistent.
 *
 * Guard: only clear if the module still points to *this* feature — if
 * addItem already replaced this panel with a new feature, selectedFeatureId
 * will have changed and we must not touch it.
 */
onUnmounted(() => {
  if (props.mapModule.selectedFeatureId === props.featureId) {
    props.mapModule.selectedFeatureId = null
  }
})
</script>
